import { Control, Controller, FieldError, UseFormSetValue } from "react-hook-form";
import { useCallback } from "react";
import { useDropzone } from "react-dropzone";
import { Box, Typography } from "@mui/material";
import DropzoneFieldStyles from "./DropzoneField.styles";
import { useTranslation } from "react-i18next";

interface DropZoneFieldProps {
    name: string,
    multiple?: boolean,
    control: Control<any>,
    setValue: UseFormSetValue<any>,
}

interface DropzoneProps {
    onChange: (...event: any[]) => void,
    multiple: boolean,
    name: string,
    setValue: UseFormSetValue<any>,
    error?: FieldError,

}

export const DropzoneField = ({
    name,
    control,
    multiple,
    setValue,
    ...rest
}: DropZoneFieldProps) => {
    return (
        <Controller
        render={({ field: { onChange, value }, fieldState: { error } }) => (
            <Box>
                <Dropzone
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        const files = Array.from(e.target.files || []);
                        const newFiles = multiple ? [...(value || []), ...files] : files[0];
                        onChange(newFiles);
                        setValue(name, newFiles);
                    }}
                        name={name}
                        error={error}
                        multiple={!!multiple}
                        setValue={setValue}
                        {...rest}
                    />
                    {
                        error &&
                        <Typography variant='caption' color='error'>{error?.message}</Typography>
                    }
                </Box>
            )}
            name={name}
            control={control}
            defaultValue={null}
        />
    );
};

const Dropzone = ({ onChange, setValue, name, error, ...rest }: DropzoneProps) => {
    const classes = DropzoneFieldStyles();
    const { t } = useTranslation()

    const onDrop = useCallback((acceptedFiles: File[]) => {
        onChange({ target: { files: acceptedFiles } } as any);
    }, [onChange]);
    
    const { getRootProps, getInputProps, isDragActive } = useDropzone({ onDrop, ...rest });

    return (
        <Box {...getRootProps()}
            className={`${classes.dropzoneBox} ${isDragActive ? classes.borderDrag2 : classes.border} ${error && classes.borderError}`}
        >
            <input {...getInputProps({ onChange })} />
            {isDragActive ? (
                <p>{t("Arrastre su archivo aquí")}</p>
            ) : (
                <p>{t(`Arraste un ${rest.multiple ? 'o varios' : ''} archivo${rest.multiple ? 's' : ''} o haga click para abrir el explorador`)}</p>
            )}
        </Box>
    );
};

interface DropzoneUncontrolledProps {
    onChange: (files: File[]) => void,
    multiple?: boolean,
    error?: boolean,
    disabled?: boolean,
}

export const DropzoneUncontrolled = ({ onChange, error, ...rest }: DropzoneUncontrolledProps) => {
    const classes = DropzoneFieldStyles();
    const { t } = useTranslation()

    const onDrop = useCallback((acceptedFiles: File[]) => {
        onChange(acceptedFiles);
    }, []);

    const onDropzoneChange = (e: any) => onChange(e.target.files);

    const { getRootProps, getInputProps, isDragActive } = useDropzone({ onDrop, ...rest });

    return (
        <Box {...getRootProps()}
            className={`${classes.dropzoneBox} ${isDragActive ? classes.borderDrag2 : classes.border} ${error && classes.borderError} ${rest.disabled && classes.dropzoneBoxDisabled}`}
        >
            <input {...getInputProps({ onChange: onDropzoneChange })} />
            {isDragActive ? (
                <p>{t("ArrasteAqui")}</p>
            ) : (
                <p>{t("ArrasteAquiOuCliqueParaSelecionarOsArquivos")}</p>
            )}
        </Box>
    );
};
